import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CalendarDays, ChartColumn, Download, RefreshCw } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatReportDate, formatReportMonth, getPresetRange, validateDateRange } from '../utils/caseStatistics';
import { API_ENDPOINTS } from '../config/api';
import './StatisticsPage.css';

const METRICS = [
  { key: 'created', label: 'Cases created', tone: 'created' },
  { key: 'approved', label: 'Approved', tone: 'approved' },
  { key: 'denied', label: 'Denied', tone: 'denied' },
  { key: 'pending', label: 'Pending', tone: 'pending' },
];
const countFormat = new Intl.NumberFormat('en-US');

const COUNT_KEYS = [...METRICS.map((metric) => metric.key), 'other'];
const validCounts = (counts) => counts && COUNT_KEYS.every((key) => Number.isSafeInteger(counts[key]) && counts[key] >= 0)
  && counts.created === counts.approved + counts.denied + counts.pending + counts.other;
const validReport = (data) => {
  if (!validCounts(data?.counts) || !Number.isSafeInteger(data.totalTableCases) || data.totalTableCases < 0
    || !Number.isSafeInteger(data.missingDates) || data.missingDates < 0
    || !Number.isSafeInteger(data.outsideRangeCases) || data.outsideRangeCases < 0
    || typeof data.databaseName !== 'string' || data.timeZone !== 'America/New_York'
    || !data.generatedAtUtc || Number.isNaN(new Date(data.generatedAtUtc).getTime())) return false;
  if (data.totalTableCases !== data.counts.created + data.outsideRangeCases + (data.startDate ? data.missingDates : 0)) return false;
  return ['months', 'counties', 'roles'].every((key) => Array.isArray(data[key])
    && data[key].every((group) => typeof group.name === 'string' && validCounts(group.counts))
    && COUNT_KEYS.every((metric) => data[key].reduce((sum, group) => sum + group.counts[metric], 0) === data.counts[metric]));
};

const StatisticsPage = ({ getAccessToken, onBack }) => {
  const reportElement = useRef(null);
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState(() => getPresetRange('month'));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const range = period === 'all' ? { start: '', end: '' } : period === 'custom' ? customRange : getPresetRange(period);
  const rangeError = period === 'all' ? '' : validateDateRange(range.start, range.end);
  const reportMatchesRange = report && (report.startDate || '') === range.start && (report.endDate || '') === range.end;
  const available = !loading && !error && !rangeError && reportMatchesRange;
  const rangeLabel = period === 'all' ? 'All time' : rangeError ? 'Custom date range' : `${formatReportDate(range.start)} - ${formatReportDate(range.end)}`;
  const statusRows = [...METRICS.slice(1), ...(report?.counts.other ? [{ key: 'other', label: 'Other status', tone: 'other' }] : [])];
  const refreshReport = () => setRefreshVersion((version) => version + 1);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setReport(null);
    setExportError('');
    setError('');
    if (rangeError) {
      setLoading(false);
      return () => { active = false; controller.abort(); };
    }
    setLoading(true);
    const loadStatistics = async () => {
      try {
        const tokenResponse = await getAccessToken();
        if (!tokenResponse || !active) return;
        const response = await fetch(API_ENDPOINTS.CASE_STATISTICS(range.start, range.end), {
          credentials: 'include', cache: 'no-store', signal: controller.signal,
          headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
        });
        if (!response.ok) throw new Error(response.status === 404
          ? 'The statistics API is not available. Deploy the updated backend.'
          : `Unable to load statistics (HTTP ${response.status}). Please try again.`);
        const data = await response.json();
        if (!validReport(data) || (data.startDate || '') !== range.start || (data.endDate || '') !== range.end)
          throw new Error('The statistics response did not reconcile. Please refresh or check the backend.');
        if (active) setReport(data);
      } catch (failure) {
        if (active && failure.name !== 'AbortError') {
          console.error('Statistics API request failed:', failure);
          setError(failure.message || 'Unable to load statistics. Please try again.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    loadStatistics();
    return () => { active = false; controller.abort(); };
  }, [getAccessToken, range.start, range.end, rangeError, refreshVersion]);

  const exportPdf = async () => {
    if (!available || exporting || !reportElement.current) return;
    setExporting(true);
    setExportError('');
    try {
      const element = reportElement.current;
      if (document.fonts?.ready) await document.fonts.ready;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const imageWidth = pdf.internal.pageSize.getWidth() - margin * 2;
      const imageHeight = pdf.internal.pageSize.getHeight() - margin * 2 - 8;
      // Capture at a width that matches the printed page (not the browser's on-screen width),
      // so the report reflows into its compact, print-friendly layout and renders at a
      // natural, legible size on paper instead of being shrunk down to fit a narrow page.
      const printWidth = Math.round(imageWidth * (96 / 25.4));
      const padding = parseFloat(getComputedStyle(element).paddingLeft) + parseFloat(getComputedStyle(element).paddingRight);
      const captureWidth = Math.ceil(Math.max(printWidth,
        ...Array.from(element.querySelectorAll('table'), (table) => table.scrollWidth + padding)));
      const protectedBlocks = [];
      const canvas = await html2canvas(element, {
        backgroundColor: '#f8faf9', scale: 2, useCORS: true, logging: false,
        width: captureWidth, windowWidth: captureWidth,
        onclone: (clonedDocument) => {
          // Inline the page's CSS so the capture renders correctly even if the cloned
          // document's <link rel="stylesheet"> fails to (re)load - e.g. in production,
          // where the stylesheet is referenced by a relative path that only resolves
          // against the original page URL, the clone otherwise falls back to
          // unstyled, text-only markup.
          Array.from(document.styleSheets).forEach((sheet) => {
            try {
              const cssText = Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
              if (cssText) {
                const styleTag = clonedDocument.createElement('style');
                styleTag.textContent = cssText;
                clonedDocument.head.appendChild(styleTag);
              }
            } catch (styleFailure) {
              // Cross-origin stylesheets can't be read here; their <link> tag is still cloned.
            }
          });
          const clone = clonedDocument.querySelector('[data-statistics-export]');
          clone.style.width = `${captureWidth}px`;
          clone.style.minHeight = '0';
          clone.querySelectorAll('.statistics-table-scroll').forEach((tableContainer) => {
            tableContainer.style.overflow = 'visible';
            tableContainer.scrollLeft = 0;
          });
          const top = clone.getBoundingClientRect().top;
          clone.querySelectorAll('.statistics-header, .statistics-report-heading, .statistics-metrics, .statistics-source, .statistics-definition, .statistics-warning, .statistics-breakdown, .statistics-monthly h2, tr').forEach((block) => {
            const bounds = block.getBoundingClientRect();
            protectedBlocks.push({ start: bounds.top - top, end: bounds.bottom - top });
          });
        },
      });
      if (!canvas.width || !canvas.height) throw new Error('The report capture was empty.');
      const pixelsPerPage = Math.floor(canvas.width * imageHeight / imageWidth);
      const captureScale = canvas.width / captureWidth;
      let position = 0;
      while (position < canvas.height) {
        let end = Math.min(position + pixelsPerPage, canvas.height);
        for (const block of protectedBlocks) {
          const start = Math.floor(block.start * captureScale);
          const bottom = Math.ceil(block.end * captureScale);
          if (start > position && start < end && bottom > end && bottom - start <= pixelsPerPage) end = start;
        }
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = end - position;
        const context = slice.getContext('2d');
        if (!context) throw new Error('The browser could not create a PDF image.');
        context.drawImage(canvas, 0, position, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        if (position > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', margin, margin, imageWidth, slice.height * imageWidth / canvas.width);
        position = end;
      }
      const totalPages = pdf.getNumberOfPages();
      for (let page = 1; page <= totalPages; page += 1) {
        pdf.setPage(page);
        pdf.setFontSize(9);
        pdf.setTextColor(90, 100, 108);
        pdf.text(`DUI Program | Statistics | Page ${page} of ${totalPages}`, margin, pdf.internal.pageSize.getHeight() - margin);
      }
      await pdf.save(period === 'all' ? 'dui-case-statistics-all-time.pdf' : `dui-case-statistics-${range.start}-to-${range.end}.pdf`, { returnPromise: true });
    } catch (exportFailure) {
      console.error('Statistics PDF export failed:', exportFailure);
      setExportError('The PDF could not be exported. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <main className="statistics-page" ref={reportElement} data-statistics-export>
      <div className="statistics-content">
        <button className="statistics-back" onClick={onBack} disabled={exporting} data-html2canvas-ignore><ArrowLeft size={18} aria-hidden="true" />Admin Dashboard</button>
        <header className="statistics-header">
          <div>
            <p className="statistics-eyebrow">DUI FIRST TIME OFFENDER PROGRAM</p>
            <h1><ChartColumn size={28} aria-hidden="true" />Case Statistics</h1>
          </div>
          <div className="statistics-actions" data-html2canvas-ignore>
            <button className="statistics-button" onClick={refreshReport} disabled={loading || !!rangeError || exporting} title="Refresh database statistics"><RefreshCw size={18} aria-hidden="true" />Refresh</button>
            <button className="statistics-button statistics-primary" onClick={exportPdf} disabled={!available || exporting}><Download size={18} aria-hidden="true" />{exporting ? 'Exporting...' : 'Export PDF'}</button>
          </div>
        </header>

        <section className="statistics-filters" aria-label="Reporting period" data-html2canvas-ignore>
          <div className="statistics-periods" role="group" aria-label="Date range presets">
            {[['month', 'This month'], ['year', 'This year'], ['all', 'All time'], ['custom', 'Custom range']].map(([value, label]) => (
              <button key={value} disabled={exporting} aria-pressed={period === value} onClick={() => { setPeriod(value); setExportError(''); }}>{label}</button>
            ))}
          </div>
          {period === 'custom' ? (
            <div className="statistics-date-fields">
              <label htmlFor="statistics-start">Start date<input id="statistics-start" type="date" disabled={exporting} value={customRange.start} aria-invalid={!!rangeError} aria-describedby={rangeError ? 'statistics-range-error' : undefined} onChange={(event) => setCustomRange({ ...customRange, start: event.target.value })} /></label>
              <label htmlFor="statistics-end">End date<input id="statistics-end" type="date" disabled={exporting} value={customRange.end} min={customRange.start || undefined} aria-invalid={!!rangeError} aria-describedby={rangeError ? 'statistics-range-error' : undefined} onChange={(event) => setCustomRange({ ...customRange, end: event.target.value })} /></label>
            </div>
          ) : <span className="statistics-period-note">{period === 'all' ? 'Entire Cases table' : period === 'month' ? 'Month to date' : 'Year to date'}</span>}
          {rangeError && <p id="statistics-range-error" className="statistics-error" role="alert">{rangeError}</p>}
        </section>

        {exportError && <p className="statistics-error" role="alert">{exportError}</p>}
        {loading || (!rangeError && !error && !reportMatchesRange) ? <div className="statistics-state" role="status">Loading case statistics...</div>
          : error ? <div className="statistics-state" role="alert"><p>{error}</p><button className="statistics-button" onClick={refreshReport}>Try Again</button></div>
          : available && (
            <div aria-live="polite">
              <div className="statistics-report-heading"><h2>Period overview</h2><span><CalendarDays size={17} aria-hidden="true" />{rangeLabel}</span></div>
              <div className="statistics-metrics">
                {METRICS.map((metric) => (
                  <article className={`statistics-metric statistics-${metric.tone}`} key={metric.key}>
                    <h3>{metric.label}</h3><strong>{countFormat.format(report.counts[metric.key])}</strong>
                    <span>{metric.key === 'created' ? 'Total applications' : 'Current case status'}</span>
                  </article>
                ))}
              </div>
              <p className="statistics-definition">Cases created in this period, grouped by current status. Approved includes the system status Success. Dates use Eastern time.</p>
              <dl className="statistics-source">
                <div><dt>Source database</dt><dd>{report.databaseName}</dd></div>
                <div><dt>Cases table rows scanned</dt><dd>{countFormat.format(report.totalTableCases)}</dd></div>
                <div><dt>In selected period</dt><dd>{countFormat.format(report.counts.created)}</dd></div>
                <div><dt>Outside selected period</dt><dd>{countFormat.format(report.outsideRangeCases)}</dd></div>
                <div><dt>Database snapshot (ET)</dt><dd>{new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(new Date(report.generatedAtUtc))}</dd></div>
              </dl>
              {report.missingDates > 0 && <p className="statistics-warning">{countFormat.format(report.missingDates)} records have no valid creation date and are {period === 'all' ? 'included in all-time totals.' : 'excluded from this date range.'}</p>}
              {report.counts.created === 0 ? <div className="statistics-state">No cases were created in this period.</div> : (
                <section className="statistics-breakdown" aria-labelledby="statistics-status-title">
                  <div><h2 id="statistics-status-title">Status breakdown</h2><p>Of {countFormat.format(report.counts.created)} cases created</p></div>
                  <div className="statistics-bars">
                    {statusRows.map((metric) => {
                      const percentage = report.counts[metric.key] / report.counts.created * 100;
                      return <div className={`statistics-bar-row statistics-${metric.tone}`} key={metric.key}>
                        <span>{metric.label}</span><div className="statistics-bar-track" aria-hidden="true"><div style={{ width: `${percentage}%` }} /></div><strong>{countFormat.format(report.counts[metric.key])}</strong><span>{percentage.toFixed(1)}%</span>
                      </div>;
                    })}
                  </div>
                </section>
              )}
              <section className="statistics-monthly" aria-labelledby="statistics-monthly-title">
                <h2 id="statistics-monthly-title">Monthly breakdown</h2>
                <div className="statistics-table-scroll" tabIndex="0" role="region" aria-label="Monthly case statistics">
                  <table>
                    <caption className="statistics-sr-only">Cases created and their current statuses by month, {rangeLabel}</caption>
                    <thead><tr><th scope="col">Month</th>{METRICS.map((metric) => <th scope="col" key={metric.key}>{metric.label}</th>)}{report.counts.other > 0 && <th scope="col">Other status</th>}</tr></thead>
                    <tbody>{report.months.length ? report.months.map((row) => <tr key={row.name}><th scope="row">{row.name === 'Undated' ? row.name : formatReportMonth(row.name)}</th>{METRICS.map((metric) => <td key={metric.key}>{countFormat.format(row.counts[metric.key])}</td>)}{report.counts.other > 0 && <td>{row.counts.other}</td>}</tr>) : <tr><td colSpan={5}>No cases in this period.</td></tr>}</tbody>
                    <tfoot><tr><th scope="row">Total</th>{METRICS.map((metric) => <td key={metric.key}>{countFormat.format(report.counts[metric.key])}</td>)}{report.counts.other > 0 && <td>{report.counts.other}</td>}</tr></tfoot>
                  </table>
                </div>
              </section>
              {[['counties', 'County of arrest', report.counties], ['roles', 'Applicant role', report.roles]].map(([key, title, groups]) => (
                <section className="statistics-monthly" key={key} aria-labelledby={`statistics-${key}-title`}>
                  <h2 id={`statistics-${key}-title`}>{title}</h2>
                  <div className="statistics-table-scroll" tabIndex="0" role="region" aria-label={`${title} case statistics`}>
                    <table>
                      <caption className="statistics-sr-only">Cases by {title.toLowerCase()}, {rangeLabel}</caption>
                      <thead><tr><th scope="col">{title}</th>{METRICS.map((metric) => <th scope="col" key={metric.key}>{metric.label}</th>)}{report.counts.other > 0 && <th scope="col">Other status</th>}</tr></thead>
                      <tbody>{groups.length ? groups.map((group) => <tr key={group.name}><th scope="row">{group.name}</th>{METRICS.map((metric) => <td key={metric.key}>{countFormat.format(group.counts[metric.key])}</td>)}{report.counts.other > 0 && <td>{group.counts.other}</td>}</tr>) : <tr><td colSpan={5}>No cases in this period.</td></tr>}</tbody>
                      <tfoot><tr><th scope="row">Total</th>{METRICS.map((metric) => <td key={metric.key}>{countFormat.format(report.counts[metric.key])}</td>)}{report.counts.other > 0 && <td>{report.counts.other}</td>}</tr></tfoot>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )}
      </div>
    </main>
  );
};

export default StatisticsPage;